import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Subscriber } from './models';
import { baseApi } from '@centerpiece/components';

export const subscriber = baseApi
  .enhanceEndpoints({ addTagTypes: ['Subscribers', 'Subscriber'] })
  .injectEndpoints({
    endpoints: builder => ({
      getSubscribers: builder.query<Subscriber[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'subscriber',
          method: 'GET',
          params: params ? { nested: 'all', ...params } : { nested: 'all' },
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'Subscriber' as const,
                  id,
                })),
                'Subscribers',
              ]
            : ['Subscribers'],
      }),
      getSubscriber: builder.query<Subscriber, { id: number; params?: object }>(
        {
          query: ({ id, params }: { id: number; params?: object }) => ({
            url: `subscriber/${id}`,
            method: 'GET',
            params: params ? { nested: 'all', ...params } : { nested: 'all' },
          }),
          providesTags: (result, error, arg) => [
            { type: 'Subscriber', id: arg.id },
          ],
          // configuration for an individual endpoint, overriding the api setting
          keepUnusedDataFor: 90,
        }
      ),
      createSubscriber: builder.mutation({
        query: request => ({
          url: 'subscriber',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Subscribers'],
      }),
      updateSubscriber: builder.mutation<Subscriber, Subscriber>({
        query: request => ({
          url: `subscriber/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Subscriber', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            subscriber.util.updateQueryData('getSubscriber', { id }, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(subscriber.util.invalidateTags(['Subscribers']))
             */
            dispatch(subscriber.util.invalidateTags(['Subscribers']));
          }
        },
      }),
      deleteSubscriber: builder.mutation<Subscriber, number>({
        query: id => ({
          url: `subscriber/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Subscriber' as const, arg }, 'Subscribers']
            : ['Subscribers'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetSubscribersQuery,
  useGetSubscriberQuery,
  useCreateSubscriberMutation,
  useUpdateSubscriberMutation,
  useDeleteSubscriberMutation,
  usePrefetch: useSubscribersPrefetch,
} = subscriber;
const selectSubscribersRoot = subscriber.endpoints.getSubscribers.select({});
export const selectSubscribers = createSelector(
  selectSubscribersRoot,
  subscriber => subscriber.data || []
);
